import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import React, { useMemo, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import QrCodeIcon from "@mui/icons-material/QrCode";
import ShareIcon from "@mui/icons-material/Share";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

type TShareMenuProps = {
  value: string;
  handleShowQRCode: () => void;
  enableWhatsAppShare?: boolean;
  enableNativeShare?: boolean;
  enableCopy?: boolean;
};

const ShareMenu = ({
  value,
  handleShowQRCode,
  enableWhatsAppShare = true,
  enableNativeShare = true,
  enableCopy = true,
}: TShareMenuProps) => {
  const [shareMenuAnchor, setShareMenuAnchor] = useState<null | HTMLElement>(null);

  const handleShareMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setShareMenuAnchor(event.currentTarget);
  };

  const handleShareMenuClose = () => {
    setShareMenuAnchor(null);
  };

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      handleShareMenuClose();
    } catch (error) {
      console.error("Failed to copy address:", error);
    }
  };

  const handleQRCodeClicked = () => {
    handleShowQRCode();
    handleShareMenuClose();
  };

  const handleWhatsAppShare = () => {
    const text = `Transfer tokens to: ${value}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, "_blank");
    handleShareMenuClose();
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Transfer tokens",
          text: `Transfer tokens to: ${value}`,
          url: value || "",
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    }
    handleShareMenuClose();
  };

  const menuItems = useMemo(() => {
    const items = [
      {
        isEnabled: enableCopy,
        label: "Copy Address",
        icon: <ContentCopyIcon fontSize="small" />,
        onClick: handleCopyAddress,
      },
      {
        isEnabled: true,
        label: "Show QR Code",
        icon: <QrCodeIcon fontSize="small" />,
        onClick: handleQRCodeClicked,
      },
      {
        isEnabled: enableWhatsAppShare,
        label: "Share via WhatsApp",
        icon: <WhatsAppIcon fontSize="small" />,
        onClick: handleWhatsAppShare,
      },
      {
        isEnabled: enableNativeShare && navigator.share,
        label: "Share",
        icon: <ShareIcon fontSize="small" />,
        onClick: handleNativeShare,
      },
    ].filter((item) => item.isEnabled);

    return items;
  }, [enableCopy, enableNativeShare, enableWhatsAppShare, handleShowQRCode]);

  return (
    <>
      <IconButton
        onClick={handleShareMenuOpen}
        sx={{
          "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.04)" },
        }}
      >
        <ShareIcon />
      </IconButton>

      {/* Share menu */}
      <Menu
        anchorEl={shareMenuAnchor}
        open={Boolean(shareMenuAnchor)}
        onClose={handleShareMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {menuItems.map((item) => (
          <MenuItem key={item.label} onClick={item.onClick}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ShareMenu;
